/**
 * @fileoverview Web Audio: SFX и амбиент для ATC-симулятора.
 *
 * Нужно вызвать {@link SoundManager.init} (и желательно {@link SoundManager.resume})
 * из пользовательского жеста — браузеры приостанавливают AudioContext до этого.
 */

type AudioContextCtor = typeof AudioContext;

function getAudioContextCtor(): AudioContextCtor | null {
  const w = window as Window & { webkitAudioContext?: AudioContextCtor };
  return window.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * Процедурные UI/feedback-звуки плюс петли ветра и «комнаты ATC».
 * Менеджер звука игры; предпочтителен экспортируемый синглтон {@link Snd}.
 */
export class SoundManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private windG: GainNode | null = null;
  private ambG: GainNode | null = null;
  private on = true;
  private ready = false;

  /** Успешная инициализация — можно играть тоны. */
  get isReady(): boolean {
    return this.ready;
  }

  /** Вывод включен (мастер не в mute). */
  get isOn(): boolean {
    return this.on;
  }

  /**
   * Создать AudioContext, мастер-шину, петлю ветра и низкий амбиент.
   * Безопасно вызывать повторно; после первой успешной инициализации — no-op.
   */
  init(): void {
    if (this.ready) return;
    const AC = getAudioContextCtor();
    if (!AC) {
      this.on = false;
      return;
    }
    try {
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.on ? 0.5 : 0;
      this.master.connect(this.ctx.destination);

      // Коричневатый шум ветра через bandpass.
      const len = this.ctx.sampleRate * 2;
      const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const data = buf.getChannelData(0);
      let last = 0;
      for (let i = 0; i < len; i++) {
        const wn = Math.random() * 2 - 1;
        last = (last + 0.02 * wn) / 1.02;
        data[i] = last * 3.2;
      }
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      const flt = this.ctx.createBiquadFilter();
      flt.type = "bandpass";
      flt.frequency.value = 420;
      flt.Q.value = 0.7;
      this.windG = this.ctx.createGain();
      this.windG.gain.value = 0.05;
      src.connect(flt);
      flt.connect(this.windG);
      this.windG.connect(this.master);
      src.start();

      // Тихий гул «комнаты ATC» (два синуса).
      const o1 = this.ctx.createOscillator();
      const o2 = this.ctx.createOscillator();
      o1.type = "sine";
      o1.frequency.value = 55;
      o2.type = "sine";
      o2.frequency.value = 82.5;
      this.ambG = this.ctx.createGain();
      this.ambG.gain.value = 0.035;
      o1.connect(this.ambG);
      o2.connect(this.ambG);
      this.ambG.connect(this.master);
      o1.start();
      o2.start();

      this.ready = true;
    } catch {
      this.on = false;
    }
  }

  /** Возобновить приостановленный AudioContext (из жеста). */
  resume(): void {
    if (this.ctx && this.ctx.state === "suspended") {
      void this.ctx.resume().catch(() => {});
    }
  }

  /**
   * Включить или заглушить мастер-шину.
   * @param enabled — при true цель gain ≈ 0.5, иначе 0
   */
  set(enabled: boolean): void {
    this.on = enabled;
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(
        enabled ? 0.5 : 0,
        this.ctx.currentTime,
        0.05,
      );
    }
  }

  /**
   * Уровень амбиента ветра по нормализованной интенсивности `v` (обычно 0…1+).
   */
  wind(v: number): void {
    if (this.windG && this.ctx) {
      this.windG.gain.setTargetAtTime(0.03 + v * 0.14, this.ctx.currentTime, 0.6);
    }
  }

  /**
   * Одиночный тон с огибающей.
   * @param frequency — стартовая частота, Гц
   * @param duration — длина огибающей, с
   * @param type — форма волны
   * @param volume — пиковый gain
   * @param slideTo — опциональный экспоненциальный glide до частоты (Гц)
   */
  tone(
    frequency: number,
    duration: number,
    type: OscillatorType = "sine",
    volume = 0.16,
    slideTo?: number,
  ): void {
    if (!this.ready || !this.on || !this.ctx || !this.master) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(frequency, t);
    if (slideTo !== undefined) {
      o.frequency.exponentialRampToValueAtTime(Math.max(40, slideTo), t + duration);
    }
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(volume, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    o.connect(g);
    g.connect(this.master);
    o.start(t);
    o.stop(t + duration + 0.03);
  }

  /** Клик UI / нажатие кнопки. */
  click(): void {
    this.tone(660, 0.07, "square", 0.05);
  }

  /** Положительное подтверждение (два восходящих блипа). */
  ok(): void {
    this.tone(520, 0.09, "triangle", 0.09);
    setTimeout(() => this.tone(780, 0.14, "triangle", 0.08), 70);
  }

  /** Касание / посадка. */
  land(): void {
    this.tone(180, 0.5, "sawtooth", 0.05, 90);
  }

  /** Набор / взлет. */
  up(): void {
    this.tone(140, 0.7, "sawtooth", 0.06, 320);
  }

  /** Предупреждение / конфликт. */
  warn(): void {
    this.tone(880, 0.13, "square", 0.1);
    setTimeout(() => this.tone(660, 0.16, "square", 0.09), 150);
  }

  /** Отказ / ошибка действия. */
  bad(): void {
    this.tone(200, 0.5, "sawtooth", 0.14, 60);
  }

  /** Авария / удар. */
  crash(): void {
    this.tone(90, 1.1, "sawtooth", 0.2, 32);
    this.tone(240, 0.7, "square", 0.1, 50);
  }

  /** Уборка / снегоочиститель. */
  plow(): void {
    this.tone(120, 0.28, "square", 0.05, 150);
  }
}

/** Общий экземпляр менеджера звука. */
export const Snd = new SoundManager();
